import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, Camera, Save, X, Search, AlertCircle } from 'lucide-react';
import apiService from '../services/api';

const PatientRegistration = ({ onAddPatient }) => {
  const navigate = useNavigate();

  const [patientData, setPatientData] = useState({
    fullName: '',
    phone: '',
    age: '',
    gender: '',
    address: '',
    photo: null,
    allergies: '',
    chronicConditions: '',
    pastSurgeries: ''
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatientData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors(prev => ({
          ...prev,
          photo: 'Image size must be less than 5MB'
        }));
        return;
      }

      if (!file.type.startsWith('image/')) {
        setValidationErrors(prev => ({
          ...prev,
          photo: 'Please select a valid image file'
        }));
        return;
      }

      setPatientData(prev => ({
        ...prev,
        photo: file
      }));
    
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);

      if (validationErrors.photo) {
        setValidationErrors(prev => ({
          ...prev,
          photo: ''
        }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!patientData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (!patientData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(patientData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (!patientData.age) {
      errors.age = 'Age is required';
    } else if (parseInt(patientData.age) < 0 || parseInt(patientData.age) > 150) {
      errors.age = 'Please enter a valid age';
    }

    if (!patientData.gender) {
      errors.gender = 'Gender is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiData = {
        fullName: patientData.fullName,
        phone: patientData.phone,
        age: parseInt(patientData.age),
        gender: patientData.gender,
        address: patientData.address,
        photo: patientData.photo,
        allergies: patientData.allergies,
        chronicConditions: patientData.chronicConditions,
        pastSurgeries: patientData.pastSurgeries
      };

      console.log('Submitting patient data:', apiData);
      const result = await apiService.createPatient(apiData);
      console.log('SUCCESS - Patient created:', result);
  
      if (onAddPatient) {
        onAddPatient(result);
      }

      setSuccess(true);

      setPatientData({
        fullName: '',
        phone: '',
        age: '',
        gender: '',
        address: '',
        photo: null,
        allergies: '',
        chronicConditions: '',
        pastSurgeries: ''
      });
      setPreviewImage(null);

      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err) {
      console.error('ERROR - Failed to create patient:', err);
      console.error('Error details:', {
        message: err.message,
        validationErrors: err.validationErrors,
        stack: err.stack
      });
  
      if (err.validationErrors) {
        setValidationErrors(err.validationErrors);
        setError('Please fix the validation errors below.');
      } else {
        setError(err.message || 'Failed to register patient. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeImage = () => {
    setPatientData(prev => ({
      ...prev,
      photo: null
    }));
    setPreviewImage(null);
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-0">
        <div className="card text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Patient Registered Successfully!</h2>
          <p className="text-gray-600 mb-4">Redirecting to patient profile...</p>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-0">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Patient Registration</h1>
        <Link 
          to="/search" 
          className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors w-full sm:w-auto"
        >
          <Search className="h-4 w-4 mr-2" />
          Find Patient
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
            <p className="text-red-800 text-sm sm:text-base">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card space-y-6">
        {/* Personal Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="sm:col-span-2 sm:col-span-1">
              <label className="form-label">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={patientData.fullName}
                onChange={handleChange}
                className={`form-input ${validationErrors.fullName ? 'border-red-500' : ''}`}
                placeholder="Enter full name"
                disabled={loading}
              />
              {validationErrors.fullName && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.fullName}</p>
              )}
            </div>
          
            <div>
              <label className="form-label">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={patientData.phone}
                onChange={handleChange}
                className={`form-input ${validationErrors.phone ? 'border-red-500' : ''}`}
                placeholder="Enter phone number"
                disabled={loading}
              />
              {validationErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
              )}
            </div>
          
            <div>
              <label className="form-label">Age *</label>
              <input
                type="number"
                name="age"
                value={patientData.age}
                onChange={handleChange}
                min="0"
                max="150"
                className={`form-input ${validationErrors.age ? 'border-red-500' : ''}`}
                placeholder="Enter age"
                disabled={loading}
              />
              {validationErrors.age && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.age}</p>
              )}
            </div>
          
            <div>
              <label className="form-label">Gender *</label>
              <select
                name="gender"
                value={patientData.gender}
                onChange={handleChange}
                className={`form-input ${validationErrors.gender ? 'border-red-500' : ''}`}
                disabled={loading}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {validationErrors.gender && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.gender}</p>
              )}
            </div>
          
            <div className="sm:col-span-2">
              <label className="form-label">Address</label>
              <textarea
                name="address"
                value={patientData.address}
                onChange={handleChange}
                rows="2"
                className="form-input"
                placeholder="Enter full address"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Photo Upload - Mobile Optimized */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Photo</h2>
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="flex-shrink-0 self-center sm:self-start">
              {previewImage ? (
                <div className="relative">
                  <img 
                    src={previewImage} 
                    alt="Preview" 
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    disabled={loading}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                  <Camera className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="form-label">Upload Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 5MB</p>
              {validationErrors.photo && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.photo}</p>
              )}
            </div>
          </div>
        </div>

        {/* Medical History */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Medical History</h2>
          <div className="space-y-4">
            <div>
              <label className="form-label">Allergies</label>
              <textarea
                name="allergies"
                value={patientData.allergies}
                onChange={handleChange}
                rows="2"
                className="form-input"
                placeholder="List any allergies (e.g., Penicillin, Latex, etc.)"
                disabled={loading}
              />
            </div>
          
            <div>
              <label className="form-label">Chronic Conditions</label>
              <textarea
                name="chronicConditions"
                value={patientData.chronicConditions}
                onChange={handleChange}
                rows="2"
                className="form-input"
                placeholder="List any chronic conditions (e.g., Diabetes, Hypertension, etc.)"
                disabled={loading}
              />
            </div>
          
            <div>
              <label className="form-label">Past Surgeries</label>
              <textarea
                name="pastSurgeries"
                value={patientData.pastSurgeries}
                onChange={handleChange}
                rows="2"
                className="form-input"
                placeholder="List any past surgeries with approximate dates"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
          <Link 
            to="/" 
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-center order-2 sm:order-1"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span>Registering...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                <span>Register Patient</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PatientRegistration;